import React, { useState, useEffect } from "react";
import { FiBarChart2 } from "react-icons/fi";
import toast from "react-hot-toast";

import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";

const formatUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const formatUsd4 = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const formatTokens = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
};
const formatDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" }) + ", " + dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

export default function Usage() {
  const { organization } = useStore();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organization?._id) return;
    fetchAll();
  }, [organization?._id]);

  async function fetchAll() {
    try {
      setLoading(true);
      const [statsRes, listRes] = await Promise.all([
        api.get(`/ai-usage/stats?organisation_id=${encodeURIComponent(organization._id)}`),
        api.get(`/ai-usage?organisation_id=${encodeURIComponent(organization._id)}&limit=100`),
      ]);
      if (statsRes.ok) setStats(statsRes.data);
      if (listRes.ok) setItems(listRes.data || []);
    } catch (e) {
      console.error("Error fetching ai-usage:", e);
      toast.error(e.code || "Failed to load usage");
    } finally {
      setLoading(false);
    }
  }

  if (loading || !stats) {
    return (
      <div className="w-full p-6 flex items-center justify-center min-h-[300px]">
        <Loader />
      </div>
    );
  }

  const t = stats.totals || {};
  const avgPerMessage = t.messages ? t.cost_usd / t.messages : 0;

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-700">
          <FiBarChart2 className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usages</h1>
          <p className="text-sm text-gray-600 mt-1">
            Anthropic API spend for <span className="font-medium text-gray-800">{organization?.name || "this organisation"}</span>. Lifetime aggregates per organisation.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="TOTAL SPEND (ORG)" value={formatUsd(t.cost_usd)} hint="lifetime, server-side counter" />
        <StatCard label="MESSAGES" value={String(t.messages || 0)} hint={`${t.messages || 0} assistant messages`} />
        <StatCard
          label="TOKENS IN / OUT"
          value={`${formatTokens(t.input_tokens)} / ${formatTokens(t.output_tokens)}`}
          hint={`cache: ${formatTokens(t.cache_read_tokens)} read · ${formatTokens(t.cache_write_tokens)} write`}
        />
        <StatCard label="AVG COST / MESSAGE" value={formatUsd(avgPerMessage)} />
      </div>

      {/* By model + by feature */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Panel title="BY MODEL">
          <SimpleTable
            empty="No data"
            head={["Model", "Msgs", "In", "Out", "Cost"]}
            rows={(stats.by_model || []).map((m) => [
              m.model || "unknown",
              String(m.messages || 0),
              formatTokens(m.input_tokens),
              formatTokens(m.output_tokens),
              formatUsd4(m.cost_usd),
            ])}
          />
        </Panel>
        <Panel title="BY FEATURE">
          <SimpleTable
            empty="No data"
            head={["Feature", "Msgs", "Cost"]}
            rows={(stats.by_feature || []).map((f) => [f.feature || "unknown", String(f.messages || 0), formatUsd4(f.cost_usd)])}
          />
        </Panel>
      </div>

      {/* Recent messages */}
      <Panel title="RECENT ASSISTANT MESSAGES">
        {items.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 px-4">No assistant messages recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Feature</th>
                  <th className="px-4 py-2">Model</th>
                  <th className="px-4 py-2 text-right">In</th>
                  <th className="px-4 py-2 text-right">Out</th>
                  <th className="px-4 py-2 text-right">Cache R/W</th>
                  <th className="px-4 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.map((it) => (
                  <tr key={it._id} className="text-sm text-gray-700">
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(it.created_at)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{it.feature || "unknown"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{it.model || "unknown"}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{formatTokens(it.input_tokens)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{formatTokens(it.output_tokens)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-500">
                      {formatTokens(it.cache_read_tokens)} / {formatTokens(it.cache_write_tokens)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap font-medium">{formatUsd4(it.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}

function SimpleTable({ head, rows, empty }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-gray-500 py-6 px-4">{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
          <tr>
            {head.map((h, i) => (
              <th key={i} className={`px-4 py-2 ${i >= 1 ? "text-right" : ""}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i} className="text-sm text-gray-700">
              {r.map((c, j) => (
                <td key={j} className={`px-4 py-2 whitespace-nowrap ${j >= 1 ? "text-right" : ""} ${j === 0 ? "font-medium text-gray-900" : ""}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
